import { HomeIcon, CreditCardIcon, UserGroupIcon, ShieldCheckIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

export interface FAQCategory {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  items: FAQItem[];
}

export const categories: FAQCategory[] = [
  {
    id: 'booking',
    title: 'Booking & Reservations',
    icon: HomeIcon,
    items: [
      {
        id: 'booking-1',
        question: 'How do I make a reservation?',
        answer: 'Making a reservation is easy! Simply search for your desired location and dates, select a property, and follow the booking process. You\'ll need to confirm your booking details and make payment to secure your reservation.'
      },
      {
        id: 'booking-2',
        question: 'What is your cancellation policy?',
        answer: 'Our cancellation policy varies depending on the property and the length of stay. Most properties offer free cancellation up to 48 hours before check-in. You can find the specific cancellation policy for each property on its listing page.'
      },
      {
        id: 'booking-3',
        question: 'Can I modify my reservation dates?',
        answer: 'Yes, you can modify your reservation dates subject to availability. Log in to your account, go to your bookings, and select "Modify Reservation". Note that changes may affect the total price of your stay.'
      }
    ]
  },
  {
    id: 'payments',
    title: 'Payments & Pricing',
    icon: CreditCardIcon,
    items: [
      {
        id: 'payments-1',
        question: 'What payment methods do you accept?',
        answer: 'We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and bank transfers in select countries. All payments are processed securely through our payment partners.'
      },
      {
        id: 'payments-2',
        question: 'How does the pricing work?',
        answer: 'The total price includes the nightly rate, cleaning fee, service fee, and any applicable taxes. Prices may vary based on season, length of stay, and number of guests. You\'ll see a complete breakdown before confirming your booking.'
      }
    ]
  },
  {
    id: 'hosting',
    title: 'Hosting',
    icon: UserGroupIcon,
    items: [
      {
        id: 'hosting-1',
        question: 'How do I become a host?',
        answer: 'To become a host, create an account and click on "Become a Host" in your profile menu. You\'ll need to provide details about your property, set house rules, and upload high-quality photos. Our team will review your listing before it goes live.'
      },
      {
        id: 'hosting-2',
        question: 'What are the hosting fees?',
        answer: 'We charge a standard 3% host service fee on each booking. This covers our processing costs and platform maintenance. You can set your own nightly rates and additional fees like cleaning or pet fees.'
      }
    ]
  },
  {
    id: 'security',
    title: 'Security & Safety',
    icon: ShieldCheckIcon,
    items: [
      {
        id: 'security-1',
        question: 'How do you verify guests and hosts?',
        answer: 'We use a comprehensive verification system that includes ID verification, email confirmation, and phone verification. Hosts can also require additional verification from guests before accepting bookings.'
      },
      {
        id: 'security-2',
        question: 'Is my payment secure?',
        answer: 'Yes, all payments are processed through secure, encrypted channels. We never store your full credit card details, and all transactions are protected by bank-level security measures.'
      }
    ]
  },
  {
    id: 'support',
    title: 'Customer Support',
    icon: QuestionMarkCircleIcon,
    items: [
      {
        id: 'support-1',
        question: 'How can I contact customer support?',
        answer: 'Our customer support team is available 24/7. You can reach us through the Help Center, email support, or phone support for urgent issues. Premium members have access to priority support.'
      },
      {
        id: 'support-2',
        question: 'What should I do if I have an issue during my stay?',
        answer: 'If you experience any issues during your stay, contact your host first through our messaging system. If the issue remains unresolved, our customer support team is available to help 24/7.'
      }
    ]
  }
];